import React, { useState, useRef } from "react";
import { Play, X, ChevronRight, Video, Zap, Film } from "lucide-react";
import IPSVideo from "../../../../assets/Video/IPS-trim.mp4";

const creatorContent = [
  {
    id: 1,
    title: "IPS Glimpse",
    type: "glimpse",
    thumbnail: null,
    video: IPSVideo,
    isVideo: true,
  },
  {
    id: 2,
    title: "Morning Instant",
    type: "instant",
    thumbnail: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=300&h=400&fit=crop",
    video: null,
    isVideo: false,
  },
  {
    id: 3,
    title: "Creator Clip",
    type: "glimpse",
    thumbnail: "https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=300&h=400&fit=crop",
    video: null,
    isVideo: false,
  },
  {
    id: 4,
    title: "Live Moment",
    type: "instant",
    thumbnail: "https://images.unsplash.com/photo-1495616811223-4d98c6e9c869?w=300&h=400&fit=crop",
    video: null,
    isVideo: false,
  },
  {
    id: 5,
    title: "Studio Session",
    type: "video",
    thumbnail: "https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=300&h=400&fit=crop",
    video: null,
    isVideo: false,
  },
  {
    id: 6,
    title: "Behind Scenes",
    type: "video",
    thumbnail: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=300&h=400&fit=crop",
    video: null,
    isVideo: false,
  },
];

// Type badge config
const typeMeta = {
  glimpse: { label: "Glimpse", color: "", icon: <Video size={9} /> },
  instant: { label: "Instant", color: "", icon: <Zap size={9} /> },
  video:   { label: "Video",   color: "", icon: <Film size={9} /> },
};

// ── Video Thumbnail Card (for local video files) ──────────────────────────
const VideoThumbnailCard = ({ item, onClick }) => {
  const videoRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  const handleMouseEnter = () => {
    setHovered(true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    setHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const meta = typeMeta[item.type] || typeMeta.video;

  return (
    <button
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative flex-shrink-0 group"
      style={{ width: 96 }}
    >
      {/* Video element as thumbnail */}
      <video
        ref={videoRef}
        src={item.video}
        muted
        loop
        playsInline
        preload="metadata"
        className="h-36 w-24 rounded-lg object-cover border border-border transition-transform group-hover:scale-105"
        style={{ display: "block", pointerEvents: "none" }}
      />

      {/* Play overlay */}
      <div
        className="absolute inset-0 flex items-center justify-center rounded-lg transition-opacity"
        style={{
          background: "rgba(0,0,0,0.35)",
          opacity: hovered ? 0 : 1,
        }}
      >
        <Play size={22} color="#fff" fill="#fff" />
      </div>

      {/* Type badge */}
      <span
        className="absolute top-1.5 left-1.5 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8px] font-semibold"
        style={{ background: meta.color, color: "#111", lineHeight: 1.4 }}
      >
        {meta.icon} {meta.label}
      </span>

      {/* Title */}
      <p className="absolute bottom-1.5 left-1.5 right-1.5 truncate text-[10px] font-medium text-white drop-shadow-md">
        {item.title}
      </p>
    </button>
  );
};

// ── Image Thumbnail Card ──────────────────────────────────────────────────
const ImageThumbnailCard = ({ item, onClick }) => {
  const meta = typeMeta[item.type] || typeMeta.video;

  return (
    <button
      onClick={onClick}
      className="relative flex-shrink-0 group"
      style={{ width: 96 }}
    >
      <img
        src={item.thumbnail}
        alt={item.title}
        className="h-36 w-24 rounded-lg object-cover border border-border transition-transform group-hover:scale-105"
      />

      {/* Play overlay */}
      <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/40 opacity-0 transition-opacity group-hover:opacity-100">
        <Play size={24} className="text-foreground" fill="currentColor" />
      </div>

      {/* Type badge */}
      <span
        className="absolute top-1.5 left-1.5 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8px] font-semibold"
        style={{ background: meta.color, color: "#111", lineHeight: 1.4 }}
      >
        {meta.icon} {meta.label}
      </span>

      {/* Title */}
      <p className="absolute bottom-1.5 left-1.5 right-1.5 truncate text-[10px] font-medium text-foreground drop-shadow-md">
        {item.title}
      </p>
    </button>
  );
};

// ── Main CreatorSpaceSection ──────────────────────────────────────────────
const CreatorSpaceSection = () => {
  const [activeItem, setActiveItem]     = useState(null);
  const [showAll, setShowAll]           = useState(false);
  const modalVideoRef                   = useRef(null);

  const handleOpenItem = (item) => {
    setActiveItem(item);
  };

  const handleCloseItem = () => {
    if (modalVideoRef.current) {
      modalVideoRef.current.pause();
    }
    setActiveItem(null);
  };

  return (
    <>
      {/* Creator Space Container */}
      <div
        className="animate-slide-up rounded-xl bg-card p-4"
        style={{
            width: "100%",
            border: "1px solid var(--border-color, rgb(52, 51, 51))",
            marginBottom: 16,
            marginLeft: "auto",
            marginRight: "auto",
            backgroundColor: "rgba(36,36,45,0.2)",
        }}
        >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center rounded-lg"
              style={{ width: 26, height: 26, background: "white" }}
            >
              <Video size={13} color="black" />
            </div>
            <h3 className="text-sm font-semibold text-foreground">Creator Space</h3>
          </div>
          <span className="text-[10px] text-muted-foreground px-2 py-0.5 rounded-full"
            style={{ background: "rgba(167,139,250,0.12)", color: "white" }}
          >
            Kleido Originals
          </span>
        </div>

        {/* Scrollable row */}
        <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
          {creatorContent.map((item) =>
            item.isVideo ? (
              <VideoThumbnailCard
                key={item.id}
                item={item}
                onClick={() => handleOpenItem(item)}
              />
            ) : (
              <ImageThumbnailCard
                key={item.id}
                item={item}
                onClick={() => handleOpenItem(item)}
              />
            )
          )}

          {/* Show All Button */}
          <button
            onClick={() => setShowAll(true)}
            className="relative flex-shrink-0 group"
            style={{ width: 96 }}
          >
            <div
              className="h-36 w-24 rounded-lg flex items-center justify-center transition-all"
              style={{
                border: "2px dashed var(--border-color, rgb(72,71,71))",
                background: "rgba(255,255,255,0.03)",
              }}
            >
              <div className="flex flex-col items-center gap-1">
                <ChevronRight size={20} className="text-foreground/70" />
                <p className="text-[10px] font-medium text-foreground/70">Show All</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* ── Video/Image Player Modal ── */}
      {activeItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={handleCloseItem}
        >
          <div
            className="relative rounded-2xl border border-border bg-card overflow-hidden"
            style={{ width: "100%", maxWidth: 360 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={handleCloseItem}
              className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-foreground transition-colors"
              style={{ background: "rgba(0,0,0,0.5)" }}
            >
              <X size={18} />
            </button>

            {/* Media */}
            {activeItem.isVideo ? (
              <video
                ref={modalVideoRef}
                src={activeItem.video}
                controls
                autoPlay
                className="w-full object-cover"
                style={{ aspectRatio: "9/16", maxHeight: "75vh" }}
              />
            ) : (
              <img
                src={activeItem.thumbnail}
                alt={activeItem.title}
                className="w-full object-cover"
                style={{ aspectRatio: "9/16" }}
              />
            )}

            {/* Info overlay */}
            <div
              className="absolute bottom-0 left-0 right-0 p-4"
              style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent)" }}
            >
              <div className="flex items-center gap-2 mb-0.5">
                <span
                  className="text-[9px] font-semibold rounded-full px-2 py-0.5"
                  style={{
                    background: typeMeta[activeItem.type]?.color || "#a78bfa",
                    color: "#111",
                  }}
                >
                  {typeMeta[activeItem.type]?.label || "Video"}
                </span>
              </div>
              <p className="text-sm font-semibold text-white">{activeItem.title}</p>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
                Oowlet Creator
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Show All Modal ── */}
      {showAll && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm p-4"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setShowAll(false)}
        >
          <div
            className="relative w-full rounded-2xl border border-border bg-card overflow-hidden flex flex-col"
            style={{ maxWidth: 860, maxHeight: "90vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-border p-4">
              <div className="flex items-center gap-2">
                <Video size={16} style={{ color: "#a78bfa" }} />
                <h2 className="text-base font-semibold text-foreground">All Creator Content</h2>
              </div>
              <button
                onClick={() => setShowAll(false)}
                className="rounded-full p-1.5 text-foreground transition-colors"
                style={{ background: "rgba(255,255,255,0.08)" }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Grid */}
            <div className="overflow-y-auto flex-1 p-5">
              <div className="grid grid-cols-2 gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }}>
                {creatorContent.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      handleOpenItem(item);
                      setShowAll(false);
                    }}
                    className="relative group"
                  >
                    {item.isVideo ? (
                      <video
                        src={item.video}
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full rounded-lg object-cover border border-border transition-transform group-hover:scale-105"
                        style={{ aspectRatio: "9/16" }}
                      />
                    ) : (
                      <img
                        src={item.thumbnail}
                        alt={item.title}
                        className="w-full rounded-lg object-cover border border-border transition-transform group-hover:scale-105"
                        style={{ aspectRatio: "9/16" }}
                      />
                    )}

                    {/* Play overlay */}
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/40 opacity-0 transition-opacity group-hover:opacity-100">
                      <Play size={28} className="text-foreground" fill="currentColor" />
                    </div>

                    {/* Type badge */}
                    <span
                      className="absolute top-1.5 left-1.5 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8px] font-semibold"
                      style={{ background: typeMeta[item.type]?.color || "#a78bfa", color: "#111" }}
                    >
                      {typeMeta[item.type]?.icon} {typeMeta[item.type]?.label}
                    </span>

                    {/* Title */}
                    <p className="absolute bottom-2 left-2 right-2 truncate text-xs font-medium text-white drop-shadow-md">
                      {item.title}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreatorSpaceSection;